<script lang="ts">
  import { onMount } from 'svelte';
  import { get } from 'svelte/store';
  import { page, router } from '@inertiajs/svelte';
  import type { CalendarEvent } from '../lib/types';
  import {
    parseCalendarDate,
    sortEventsForDisplay,
    getLocalToday,
    toDateStr,
    formatCalendarDate,
    getTodayStr,
  } from '../lib/utils';
  import { eventModalOpen, isShortcutsMinimized, toggleShortcutsBar } from '../lib/stores/ui';
  import { dayCellPopoverState } from '../lib/stores/dayCellPopover';
  import { favoriteViews } from '../lib/stores/favorites';
  import { navigateToFavorite } from '../lib/favoritesNavigation';
  import { isDialogOpen, shouldHandleTodayHotkey } from '../lib/pageHotkeys';
  import EventsCalendarHeader from '../components/EventsCalendarHeader.svelte';
  import MobileTopBar from '../components/mobile/MobileTopBar.svelte';
  import SegmentedControl from '../components/mobile/SegmentedControl.svelte';
  import MobileMonthView from '../components/mobile/MobileMonthView.svelte';
  import MobileAgendaView from '../components/mobile/MobileAgendaView.svelte';
  import DesktopOnlyNotice from '../components/DesktopOnlyNotice.svelte';
  import FavoriteStar from '../components/FavoriteStar.svelte';
  import { viewport } from '../lib/stores/viewport';
  import type { CalendarLayout } from '../lib/types';
  import BigYearView from '../components/BigYearView.svelte';
  import SequentialCalendarView from '../components/SequentialCalendarView.svelte';
  import WeekdayCalendarView from '../components/WeekdayCalendarView.svelte';
  import WallCalendarView from '../components/WallCalendarView.svelte';
  import TimeBlocksCalendarView from '../components/TimeBlocksCalendarView.svelte';
  import {
    MOBILE_CALENDAR_KINDS,
    THREE_DAYS,
    addDays,
    mobileCalendarUrl,
    resolveMobileCalendar,
    shiftMonth,
    type MobileCalendarKind,
  } from '../lib/mobileCalendar';
  import EventModal from '../components/EventModal.svelte';
  import EventMoveConfirmModal from '../components/EventMoveConfirmModal.svelte';
  import DayCellPopover from '../components/DayCellPopover.svelte';
  import HoveredDateLabel from '../components/HoveredDateLabel.svelte';

  interface Props {
    events: CalendarEvent[];
    year: number;
    layout: string;
    validLayouts: string[];
    defaultLayout: string;
    timeBlocksStart: string;
    timeBlocksRange: number;
    /** The layout named in the URL, null when it names none (a phone then opens Month). */
    requestedLayout?: string | null;
    /** Month a phone's Month layout opens on (1–12), null for the current one. */
    month?: number | null;
  }

  let {
    events,
    year,
    layout: layoutProp,
    validLayouts,
    defaultLayout,
    timeBlocksStart,
    timeBlocksRange,
    requestedLayout = null,
    month: monthProp = null,
  }: Props = $props();

  // `agenda` is a phone-only layout; a desktop window opening its URL shows Wall.
  let layout = $derived(layoutProp === 'agenda' && !$viewport.isMobile ? 'wall' : layoutProp);

  // Phone layout (Month · Agenda · 3 Days), derived from the URL only: it never
  // touches a stored preference. Month steps within the loaded year; the shown
  // month resets when the URL's year or month changes.
  let mobileView = $derived(resolveMobileCalendar(layoutProp, requestedLayout));
  let currentMonth = $derived(Number(getTodayStr().slice(5, 7)));
  let shownMonth = $derived(
    monthProp ?? (year === Number(getTodayStr().slice(0, 4)) ? currentMonth : 1),
  );
  let todayNonce = $state(0);

  // Build events-by-date map for all views
  function buildEventsByDate(evts: CalendarEvent[]): Map<string, CalendarEvent[]> {
    const map = new Map<string, CalendarEvent[]>();
    for (const event of evts) {
      const existing = map.get(event.date) || [];
      existing.push(event);
      map.set(event.date, existing);

      // For multi-day events, also add to each intermediate day
      if (event.endDate && event.endDate !== event.date) {
        const start = parseCalendarDate(event.date);
        const end = parseCalendarDate(event.endDate);
        const cursor = new Date(start);
        cursor.setUTCDate(cursor.getUTCDate() + 1);
        while (true) {
          if (cursor > end) break;
          const key = cursor.toISOString().slice(0, 10);
          const arr = map.get(key) || [];
          if (!arr.find((e) => e.id === event.id)) {
            arr.push(event);
            map.set(key, arr);
          }
          cursor.setUTCDate(cursor.getUTCDate() + 1);
        }
      }
    }
    // Sort events within each day
    for (const [key, dayEvents] of map) {
      map.set(key, sortEventsForDisplay(dayEvents));
    }
    return map;
  }

  let eventsByDate = $derived(buildEventsByDate(events));

  // Favorites for keyboard shortcuts
  let currentFavorites = $derived($favoriteViews);

  let isTimeBlocks = $derived(layout === 'time-blocks');

  function sundayStart(date: Date): Date {
    const d = new Date(date);
    d.setUTCDate(d.getUTCDate() - d.getUTCDay());
    return d;
  }

  // Navigation
  function buildUrl(targetYear: number, targetLayout?: string, start?: string, range?: number) {
    const l = targetLayout || layout;
    const params = new URLSearchParams();
    if (l !== defaultLayout) params.set('layout', l);
    if (l === 'time-blocks') {
      params.set('start', start || timeBlocksStart);
      params.set('range', String(range || timeBlocksRange || 7));
    }
    const query = params.toString();
    return `/events_calendar/${targetYear}${query ? `?${query}` : ''}`;
  }

  function shiftTimeBlocksStart(days: number): string {
    const d = parseCalendarDate(timeBlocksStart);
    d.setUTCDate(d.getUTCDate() + days);
    return toDateStr(d);
  }

  function mobileGo(
    kind: MobileCalendarKind,
    state: { year: number; month?: number; start?: string },
    replace = false,
  ) {
    if (replace) router.visit(mobileCalendarUrl(kind, state), { replace: true });
    else router.get(mobileCalendarUrl(kind, state), {}, { preserveScroll: true });
  }

  /** ‹ › on a phone: a month, or three days. Agenda and the notice have no period. */
  function mobileStep(direction: -1 | 1) {
    if (mobileView.kind === 'month') {
      const next = shiftMonth(year, shownMonth, direction);
      if (next.year === year) shownMonth = next.month;
      else mobileGo('month', next);
    } else if (mobileView.kind === 'three-days') {
      mobileGo('three-days', {
        year,
        start: addDays(timeBlocksStart, direction * THREE_DAYS),
      });
    }
  }

  function mobileToday() {
    const today = getTodayStr();
    const thisYear = Number(today.slice(0, 4));
    if (mobileView.kind === 'month') {
      if (year === thisYear) {
        shownMonth = currentMonth;
        todayNonce++;
      } else {
        mobileGo('month', { year: thisYear, month: currentMonth });
      }
    } else if (mobileView.kind === 'three-days') {
      if (timeBlocksStart !== today) mobileGo('three-days', { year: thisYear, start: today });
    }
  }

  /** Switching layouts on a phone only changes the URL. */
  function setMobileKind(kind: MobileCalendarKind) {
    if (mobileView.kind === kind) return;
    const today = getTodayStr();
    mobileGo(kind, {
      year: Number(today.slice(0, 4)),
      month: currentMonth,
      start: today,
    });
  }

  function goToPrevious() {
    if ($viewport.isMobile) return mobileStep(-1);
    if (isTimeBlocks) {
      router.get(
        buildUrl(year, layout, shiftTimeBlocksStart(-(timeBlocksRange || 7))),
        {},
        { preserveScroll: true },
      );
      return;
    }
    router.get(buildUrl(year - 1), {}, { preserveScroll: true });
  }

  function goToNext() {
    if ($viewport.isMobile) return mobileStep(1);
    if (isTimeBlocks) {
      router.get(
        buildUrl(year, layout, shiftTimeBlocksStart(timeBlocksRange || 7)),
        {},
        { preserveScroll: true },
      );
      return;
    }
    router.get(buildUrl(year + 1), {}, { preserveScroll: true });
  }

  function goToToday() {
    if ($viewport.isMobile) return mobileToday();
    if (isTimeBlocks) {
      const start = toDateStr(sundayStart(getLocalToday()));
      if (start !== timeBlocksStart) {
        router.get(
          buildUrl(year, layout, start, timeBlocksRange || 7),
          {},
          { preserveScroll: true },
        );
      }
      return;
    }

    const thisYear = new Date().getFullYear();
    if (thisYear !== year) {
      router.get(buildUrl(thisYear), {}, { preserveScroll: true });
    }
  }

  function setLayout(newLayout: string) {
    if (newLayout !== layout) {
      const start = toDateStr(sundayStart(getLocalToday()));
      const targetStart = newLayout === 'time-blocks' ? start : undefined;
      const targetRange = newLayout === 'time-blocks' ? 7 : undefined;
      router.get(buildUrl(year, newLayout, targetStart, targetRange), {}, { preserveScroll: true });
    }
  }

  function cycleLayout() {
    if ($viewport.isMobile) {
      const kinds = MOBILE_CALENDAR_KINDS.map((k) => k.value);
      const at = kinds.indexOf(mobileView.kind as MobileCalendarKind);
      setMobileKind(kinds[(at + 1) % kinds.length]);
      return;
    }
    const currentIdx = validLayouts.indexOf(layout);
    const next = validLayouts[(currentIdx + 1) % validLayouts.length];
    setLayout(next);
  }

  // Keyboard shortcuts — suppressed when a modal or popover is open (except ?)
  function handleKeydown(e: KeyboardEvent) {
    if (
      e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLTextAreaElement ||
      (e.target instanceof HTMLElement && e.target.isContentEditable)
    )
      return;
    if (e.key === '?') {
      toggleShortcutsBar();
      return;
    }
    if ($eventModalOpen || $dayCellPopoverState) return;
    if (shouldHandleTodayHotkey(e, { dialogOpen: isDialogOpen() })) {
      goToToday();
      return;
    }
    switch (e.key) {
      case 'ArrowLeft':
        goToPrevious();
        break;
      case 'ArrowRight':
        goToNext();
        break;
      case 'v':
      case 'V':
        cycleLayout();
        break;
      case '1':
      case '2':
      case '3':
      case '4':
      case '5':
      case '6':
      case '7':
      case '8':
      case '9':
        jumpToFavorite(parseInt(e.key) - 1);
        break;
    }
  }

  let timeBlocksRangeLabel = $derived.by(() => {
    if (!isTimeBlocks) return '';
    const start = parseCalendarDate(timeBlocksStart);
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + (timeBlocksRange || 7) - 1);
    const sm = formatCalendarDate(start, { month: 'short' });
    const em = formatCalendarDate(end, { month: 'short' });
    const sd = start.getUTCDate();
    const ed = end.getUTCDate();
    const yr = start.getUTCFullYear();
    const base = sm === em ? `${sm} ${sd}-${ed}` : `${sm} ${sd}-${em} ${ed}`;
    return `${base} (${yr})`;
  });

  // Mobile top bar: short title, grey subtitle that drops first when space is tight.
  let mobileHeading = $derived.by((): { title: string; subtitle?: string } => {
    switch (mobileView.kind) {
      case 'month':
        return {
          title: formatCalendarDate(new Date(Date.UTC(year, shownMonth - 1, 1)), { month: 'long' }),
          subtitle: String(year),
        };
      case 'agenda':
        return { title: 'Agenda', subtitle: 'Next 90 days' };
      case 'three-days': {
        const start = parseCalendarDate(timeBlocksStart);
        const end = new Date(start);
        end.setUTCDate(end.getUTCDate() + THREE_DAYS - 1);
        // Non-breaking space: wraps as "Sep 27 –" / "Oct 3", never inside a date.
        const fmt = (d: Date) =>
          `${formatCalendarDate(d, { month: 'short' })}\u00a0${d.getUTCDate()}`;
        return { title: `${fmt(start)} – ${fmt(end)}`, subtitle: String(start.getUTCFullYear()) };
      }
      default:
        return { title: String(year) };
    }
  });
  let hasMobilePeriod = $derived(mobileView.kind === 'month' || mobileView.kind === 'three-days');
  // The favorite a phone layout stands for (Month is Wall, 3 Days is Time Blocks);
  // Agenda has no desktop layout to favorite.
  let mobileFavoriteLayout = $derived<CalendarLayout | null>(
    mobileView.kind === 'month'
      ? 'wall'
      : mobileView.kind === 'three-days'
        ? 'time-blocks'
        : mobileView.kind === 'desktop-only'
          ? (layout as CalendarLayout)
          : null,
  );
  let mobileNoticeActions = $derived([
    { label: '📅 Month', onSelect: () => setMobileKind('month') },
    { label: '📋 Agenda', onSelect: () => setMobileKind('agenda') },
  ]);

  // App launch on a desktop-only favorite (flash `launch`) opens Month instead of the
  // notice. Deep links to a desktop-only layout keep it (D8).
  onMount(() => {
    if (!get(viewport).isMobile) return;
    if (get(page).flash?.launch === 'default' && mobileView.kind === 'desktop-only') {
      const today = getTodayStr();
      mobileGo('month', { year: Number(today.slice(0, 4)), month: currentMonth }, true);
    }
  });

  // URLs that load the wrong data for the layout they show are replaced. It runs on
  // every visit, not only on mount, so a favorite hotkey from this page is covered:
  // - a Time Blocks link without a 3-day window (a favorite's 7-day, Sunday-start
  //   window) opens 3 Days from today;
  // - the phone-only Agenda URL on a wide window (a pasted link, a rotated phone) opens
  //   Wall, since Agenda only loaded the next 90 days.
  // It only navigates; it writes no state, so it can't loop.
  // eslint-disable-next-line momentium/no-unguarded-effect -- navigation only (router.visit replace), no state is written; each branch is guarded by the URL it replaces, so it fires once per bad URL
  $effect(() => {
    if ($viewport.isMobile) {
      if (mobileView.kind === 'three-days' && timeBlocksRange !== THREE_DAYS) {
        const today = getTodayStr();
        mobileGo('three-days', { year: Number(today.slice(0, 4)), start: today }, true);
      }
    } else if (layoutProp === 'agenda') {
      router.visit(buildUrl(year, 'wall'), { replace: true });
    }
  });

  function jumpToFavorite(index: number) {
    const sorted = [...currentFavorites].sort((a, b) => a.position - b.position);
    const fav = sorted[index];
    if (!fav) return;
    navigateToFavorite(fav);
  }
</script>

<svelte:window onkeydown={handleKeydown} />

<div class="events-calendar-container app-min-width">
  {#if $viewport.isMobile}
    <MobileTopBar title={mobileHeading.title} subtitle={mobileHeading.subtitle}>
      {#snippet actions()}
        {#if hasMobilePeriod}
          <button
            type="button"
            class="mtb-icon-btn"
            onclick={goToPrevious}
            aria-label={mobileView.kind === 'month' ? 'Previous month' : 'Previous 3 days'}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 5-7 7 7 7" /></svg>
          </button>
          <button
            type="button"
            class="mtb-icon-btn"
            onclick={goToNext}
            aria-label={mobileView.kind === 'month' ? 'Next month' : 'Next 3 days'}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg>
          </button>
          <button type="button" class="m-cal-today" onclick={goToToday}>Today</button>
        {/if}
      {/snippet}
      {#snippet below()}
        <SegmentedControl
          label="Calendar layout"
          value={mobileView.kind === 'desktop-only' ? null : mobileView.kind}
          options={MOBILE_CALENDAR_KINDS}
          onChange={setMobileKind}
        />
        {#if mobileFavoriteLayout}
          <span class="m-cal-star">
            <FavoriteStar pageType="calendar" layout={mobileFavoriteLayout} />
          </span>
        {/if}
      {/snippet}
    </MobileTopBar>
  {:else}
    <EventsCalendarHeader
      {year}
      {layout}
      {validLayouts}
      navLabel={isTimeBlocks ? timeBlocksRangeLabel : String(year)}
      todayLabel={isTimeBlocks ? 'This Week' : 'This Year'}
      onPrevious={goToPrevious}
      onNext={goToNext}
      onToday={goToToday}
      onLayoutChange={setLayout}
    />
  {/if}

  <main
    class="events-calendar-body custom-scrollbar"
    class:no-scroll={$viewport.isMobile ? mobileView.kind !== 'desktop-only' : isTimeBlocks}
  >
    {#if $viewport.isMobile}
      {#if mobileView.kind === 'desktop-only'}
        <DesktopOnlyNotice feature={mobileView.feature} actions={mobileNoticeActions} />
      {:else if mobileView.kind === 'month'}
        {#key `${year}-${shownMonth}-${todayNonce}`}
          <MobileMonthView
            {year}
            month={shownMonth}
            {events}
            onPrevious={goToPrevious}
            onNext={goToNext}
          />
        {/key}
      {:else if mobileView.kind === 'agenda'}
        <MobileAgendaView {events} />
      {:else}
        <TimeBlocksCalendarView {events} startDate={timeBlocksStart} range={THREE_DAYS} />
      {/if}
    {:else if layout === 'big-year'}
      <BigYearView {year} {events} {eventsByDate} />
    {:else if layout === 'sequential'}
      <SequentialCalendarView {year} {events} {eventsByDate} />
    {:else if layout === 'weekday'}
      <WeekdayCalendarView {year} {events} {eventsByDate} />
    {:else if layout === 'wall'}
      <WallCalendarView {year} {events} {eventsByDate} />
    {:else if layout === 'time-blocks'}
      <TimeBlocksCalendarView {events} startDate={timeBlocksStart} range={timeBlocksRange} />
    {/if}
  </main>

  <!-- Keyboard shortcuts help (desktop only) -->
  {#if !$viewport.isMobile}
    {#if $isShortcutsMinimized}
      <button
        class="shortcuts-pill kbd-hint"
        onclick={toggleShortcutsBar}
        title="Show shortcuts (?)"
      >
        <span>?</span>
      </button>
    {:else}
      <div class="shortcuts-help kbd-hint">
        <span class="shortcut-key">B P E Y H</span> Boards • Projects • Timeline • Cycles • Habits
        <span class="sep">•</span>
        <span class="shortcut-key">← →</span>
        {isTimeBlocks ? 'Navigate week' : 'Navigate year'}
        <span class="sep">•</span>
        <span class="shortcut-key">T or 0</span> Today
        <span class="sep">•</span>
        <span class="shortcut-key">V</span> View
        <span class="sep">•</span>
        <span class="shortcut-key">K or /</span> Search
        {#if currentFavorites.length > 0}
          <span class="sep">•</span>
          <span class="shortcut-key">1-{Math.min(currentFavorites.length, 9)}</span> Favorites
        {/if}
        <button class="shortcuts-close" onclick={toggleShortcutsBar} title="Minimize (?)">×</button>
      </div>
    {/if}
  {/if}

  <DayCellPopover />
  <HoveredDateLabel />
  <EventModal monthKey="{year}-01" />
  <EventMoveConfirmModal />
</div>

<style>
  .events-calendar-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: #f9fafb;
  }

  .events-calendar-body {
    flex: 1;
    overflow: auto;
    padding: 0;
  }

  .events-calendar-body.no-scroll {
    overflow: hidden;
  }

  /* Mobile calendar header styles */
  .m-cal-today {
    height: 32px;
    margin: 0 4px;
    padding: 0 12px;
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    background: #fff;
    color: #2563eb;
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    /* 44px hit area around the 32px pill */
    position: relative;
  }

  .m-cal-today::after {
    content: '';
    position: absolute;
    inset: -6px -2px;
  }

  /* 44px hit area around the 24px star. Offsets are from the padding box, so the
     1px border needs 1px more than (44 - 24) / 2. */
  .m-cal-star :global(.star-btn) {
    position: relative;
  }

  .m-cal-star :global(.star-btn)::after {
    content: '';
    position: absolute;
    inset: -11px;
  }

  .m-cal-today:active {
    background: #f3f4f6;
  }

  .shortcuts-help {
    position: fixed;
    bottom: 0.5rem;
    left: 50%;
    transform: translateX(-50%);
    font-size: 0.575rem;
    color: #9ca3af;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(4px);
    padding: 0.25rem 0.5rem;
    border-radius: 0.375rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    border: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    white-space: nowrap;
    z-index: 50;
    line-height: 1.2;
  }

  .shortcut-key {
    font-weight: 600;
    color: #6b7280;
    background: #f3f4f6;
    padding: 0 0.2rem;
    border-radius: 0.15rem;
    font-size: 0.55rem;
  }

  .sep {
    color: #d1d5db;
    margin: 0 0.05rem;
  }

  .shortcuts-close {
    margin-left: 0.35rem;
    background: none;
    border: none;
    color: #9ca3af;
    cursor: pointer;
    font-size: 0.7rem;
    line-height: 1;
    padding: 0 0.15rem;
    border-radius: 0.15rem;
  }

  .shortcuts-close:hover {
    color: #6b7280;
    background: #f3f4f6;
  }

  .shortcuts-pill {
    position: fixed;
    bottom: 0.5rem;
    left: 50%;
    transform: translateX(-50%);
    font-size: 0.6rem;
    font-weight: 600;
    color: #9ca3af;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(4px);
    padding: 0.2rem 0.45rem;
    border-radius: 0.375rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    border: 1px solid #e5e7eb;
    cursor: pointer;
    z-index: 50;
    line-height: 1;
  }

  .shortcuts-pill:hover {
    color: #6b7280;
    background: rgba(255, 255, 255, 1);
  }
</style>
